import { Card, CardContent, Typography, Box, Divider, Link as MuiLink, CircularProgress } from '@mui/material';
import { EventBusy, CheckCircle, Warning, Schedule } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getSubstitutionStats } from '@timetable/api';
import { useAuthStore } from '@/store/authStore';
import { Link } from 'react-router-dom';

/**
 * SubstitutionWidget — Dashboard widget showing today's substitution stats
 * and tomorrow's pending absences.
 * 
 * Requirements: 23.1–23.4
 * - Shows three counters: teachers absent today, periods covered today, periods unassigned today
 * - "Tomorrow Pending" secondary row with count and link (only shown when count > 0)
 * - Neutral state: "No absences today" when no absences today or tomorrow
 * - Visible to Admin and Standard roles only
 */
export function SubstitutionWidget() {
  const session = useAuthStore((state) => state.session);
  const schoolContext = useAuthStore((state) => state.schoolContext);
  const user = useAuthStore((state) => state.user);
  const isSuperAdmin = user?.role === 'super_admin';

  // For Super Admin: schoolContext can be null (All Schools) or a specific school_id
  // For other users: use their assigned school_id
  const effectiveSchoolId = isSuperAdmin ? schoolContext : user?.school_id;

  // Fetch substitution statistics
  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard', 'substitution-stats', effectiveSchoolId ?? 'all-schools'],
    queryFn: async () => {
      if (!session?.access_token) {
        return {
          teachersAbsentToday: 0,
          periodsCoveredToday: 0,
          periodsUnassignedToday: 0,
          absencesPendingTomorrow: 0,
        };
      }

      try {
        const result = await getSubstitutionStats(
          session.access_token,
          effectiveSchoolId ?? ''
        );
        return result;
      } catch (error) {
        console.error('Substitution stats error:', error);
        return {
          teachersAbsentToday: 0,
          periodsCoveredToday: 0,
          periodsUnassignedToday: 0,
          absencesPendingTomorrow: 0,
        };
      }
    },
    retry: 1,
    enabled: !!session?.access_token,
    refetchOnMount: true,
    staleTime: 0,
  });

  // Neutral state: no absences today or tomorrow
  const hasNoAbsences = stats && 
    stats.teachersAbsentToday === 0 && 
    stats.absencesPendingTomorrow === 0;

  return (
    <Card
      sx={{
        height: '100%',
        minHeight: 150,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '4px solid',
        borderLeftColor: hasNoAbsences ? 'grey.400' : 'warning.main',
        boxShadow: '0 2px 10px rgba(15, 23, 42, 0.04)',
        position: 'relative',
        backgroundColor: 'background.paper',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          boxShadow: '0 6px 18px rgba(15, 23, 42, 0.08)',
          borderColor: hasNoAbsences ? 'grey.300' : 'warning.light',
        },
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: 0.6, fontWeight: 700, lineHeight: 1.2 }}
          >
            Today's Substitutions
          </Typography>
          <Box
            sx={{
              color: hasNoAbsences ? 'grey.500' : 'warning.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 1.5,
              backgroundColor: hasNoAbsences ? 'grey.100' : 'warning.light',
              opacity: hasNoAbsences ? 0.6 : 1,
            }}
          >
            <EventBusy />
          </Box>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
            <CircularProgress size={32} />
          </Box>
        ) : hasNoAbsences ? (
          // Neutral state (Req 23.3)
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
              No absences today
            </Typography>
          </Box>
        ) : (
          <>
            {/* Today's Stats (Req 23.1) */}
            <Box sx={{ display: 'grid', gap: 1.5, mb: 2 }}>
              {/* Teachers absent today */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    backgroundColor: 'error.light',
                    color: 'error.main',
                  }}
                >
                  <EventBusy fontSize="small" />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
                    {stats?.teachersAbsentToday || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Teachers absent
                  </Typography>
                </Box>
              </Box>

              {/* Periods covered today */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    backgroundColor: 'success.light',
                    color: 'success.main',
                  }}
                >
                  <CheckCircle fontSize="small" />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
                    {stats?.periodsCoveredToday || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Periods covered
                  </Typography>
                </Box>
              </Box>

              {/* Periods unassigned today */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    backgroundColor: 'warning.light',
                    color: 'warning.main',
                  }}
                >
                  <Warning fontSize="small" />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
                    {stats?.periodsUnassignedToday || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Periods unassigned
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Tomorrow Pending (Req 23.2) - only shown when count > 0 */}
            {stats && stats.absencesPendingTomorrow > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 36,
                      height: 36,
                      borderRadius: 1,
                      backgroundColor: 'info.light',
                      color: 'info.main',
                    }}
                  >
                    <Schedule fontSize="small" />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Tomorrow
                    </Typography>
                    <MuiLink
                      component={Link}
                      to="/absences"
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        textDecoration: 'none',
                        color: 'info.main',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {stats.absencesPendingTomorrow} {stats.absencesPendingTomorrow === 1 ? 'absence' : 'absences'} pending coverage
                    </MuiLink>
                  </Box>
                </Box>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
